import React from 'react';
import { Award, Shield, Clock, MapPin, Phone, Mail, ChevronRight, History, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { Separator } from './ui/separator';

export function AboutPage() {
  return (
    <div className="min-h-screen bg-white pb-12">
      {/* Hero Section */}
      <section className="relative h-[400px] sm:h-[500px] flex items-center justify-center overflow-hidden bg-brand-green text-white">
        <div className="absolute inset-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1573408339375-f99b292a9b27?auto=format&fit=crop&q=80')] bg-cover bg-center" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-green/80 via-brand-green/60 to-brand-green" />
        
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <div className="flex flex-col items-center justify-center mb-4 animate-in fade-in zoom-in duration-700">
            <img src="/rpk-logo.png" alt="RPK Logo" className="h-20 sm:h-24 w-auto drop-shadow-2xl brightness-110 mb-6" />
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-light tracking-[0.1em] text-brand-gold uppercase animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100">
              Ram Pratap Katta <br /> & Sons Saraf
            </h1>
          </div>
          <p className="text-xl sm:text-2xl font-serif text-white/90 mb-4 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
            A Legacy of <span className="text-brand-gold">Trust & Tradition</span>
          </p>
          <p className="text-sm sm:text-base font-serif text-brand-gold tracking-widest max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700 delay-500 uppercase">
            Crafting exquisite jewellery and timeless bonds since 1970.
          </p>
        </div>
      </section>

      {/* Heritage Section */}
      <section className="py-20 px-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="animate-in fade-in slide-in-from-left-8 duration-1000">
            <div className="flex items-center space-x-2 text-brand-green mb-4">
              <History className="h-6 w-6" />
              <span className="text-sm uppercase tracking-[0.3em] font-medium">Our Heritage</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-serif text-brand-green mb-6">Over 50 Years of Excellence in Alwar</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed text-lg">
              <p>
                Founded by <span className="text-brand-green font-medium">Ram Pratap Katta</span> in the heart of Alwar, Rajasthan, RPK Jewellery began as a small vision of providing the purest gold and finest craftsmanship to our local community.
              </p>
              <p>
                What started over five decades ago has grown into a legacy spanning three generations. Our journey is etched in the trust of thousands of families who have chosen us for their most precious moments—from weddings to celebrations of success.
              </p>
              <p>
                Today, as <span className="text-brand-green font-medium">Ram Pratap Katta & Sons Saraf</span>, we continue to uphold the same values of honesty, purity, and artistic excellence that our founder established in 1970.
              </p>
            </div>
          </div>
          
          <div className="relative animate-in fade-in zoom-in duration-1000 delay-200">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1601121141461-9d6647bca1ed?auto=format&fit=crop&q=80" 
                alt="Traditional Jewellery Crafting" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-brand-gold text-brand-green p-8 rounded-xl shadow-xl hidden sm:block">
              <p className="text-4xl font-serif mb-1">50+</p>
              <p className="text-sm uppercase tracking-widest font-bold">Years of Trust</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="bg-brand-green py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-serif text-brand-gold mb-4">Our Core Pillars</h2>
            <p className="text-white/60 max-w-2xl mx-auto uppercase tracking-widest text-sm">The foundation of every piece we create</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { 
                icon: Shield, 
                title: "Uncompromising Purity", 
                desc: "Every gram of gold and silver is BIS Hallmarked, ensuring you get exactly what you pay for, with 100% transparency." 
              },
              { 
                icon: Award, 
                title: "Master Craftsmanship", 
                desc: "We blend traditional Rajasthani techniques with modern aesthetics to create unique, timeless masterpieces." 
              },
              { 
                icon: Heart, 
                title: "Lifelong Relationships", 
                desc: "For us, you are not just a customer, but a part of the RPK family. We stand by our jewellery for a lifetime." 
              }
            ].map((value, index) => (
              <div 
                key={index}
                className="bg-white/5 border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-colors group text-center animate-in fade-in slide-in-from-bottom-8 duration-700"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="h-16 w-16 bg-brand-gold/20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <value.icon className="h-8 w-8 text-brand-gold" />
                </div>
                <h3 className="text-xl font-serif text-white mb-3">{value.title}</h3>
                <p className="text-white/70 leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visit Us Section */}
      <section className="py-20 px-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="flex items-center space-x-2 text-brand-green mb-4">
              <MapPin className="h-6 w-6" />
              <span className="text-sm uppercase tracking-[0.3em] font-medium">Visit Our Store</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-serif text-brand-green mb-8">Experience Elegance in Person</h2>
            
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="h-12 w-12 rounded-lg bg-brand-green/5 flex items-center justify-center shrink-0">
                  <MapPin className="h-6 w-6 text-brand-green" />
                </div>
                <div>
                  <h4 className="font-medium text-brand-green mb-1">Our Address</h4>
                  <p className="text-muted-foreground">
                    Shop no 148, Jai Complex, Road No. 2,<br />
                    Arya Nagar, Alwar, Rajasthan 301001
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="h-12 w-12 rounded-lg bg-brand-green/5 flex items-center justify-center shrink-0">
                  <Phone className="h-6 w-6 text-brand-green" />
                </div>
                <div>
                  <h4 className="font-medium text-brand-green mb-1">Call Us</h4>
                  <p className="text-muted-foreground">+91-9829050157</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="h-12 w-12 rounded-lg bg-brand-green/5 flex items-center justify-center shrink-0">
                  <Mail className="h-6 w-6 text-brand-green" />
                </div>
                <div>
                  <h4 className="font-medium text-brand-green mb-1">Email Us</h4>
                  <p className="text-muted-foreground">info@rpksons.com</p>
                </div>
              </div>
            </div>

            <Button className="mt-10 bg-brand-green hover:bg-brand-green/90 text-white px-8 py-6 rounded-xl text-lg group">
              Get Directions
              <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>

          <div className="h-[450px] rounded-3xl overflow-hidden shadow-2xl border-4 border-brand-green/5">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2809.1837591364115!2d76.60585127438613!3d27.56845607626241!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39729990eea0d0c5%3A0x81ff3a43c1b9a45e!2sRam%20Pratap%20Katta%20and%20Sons!5e1!3m2!1sen!2sin!4v1778128726323!5m2!1sen!2sin" 
              width="100%" 
              height="100%" 
              style={{ border: 0 }} 
              allowFullScreen={true} 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="RPK Sons Saraf Location"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto bg-brand-gold/10 rounded-3xl p-12 text-center border border-brand-gold/20">
          <h2 className="text-3xl font-serif text-brand-green mb-4">Start Your Story with Us</h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">Discover our latest collections of gold and silver jewellery crafted with passion and precision.</p>
          <Button className="bg-brand-green hover:bg-brand-green/90 text-white px-10 py-7 rounded-xl text-lg" onClick={() => window.location.href = '/'}>
            Explore Collections
          </Button>
        </div>
      </section>
    </div>
  );
}
